import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import LegacyFilterToolbar, {
    LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import {
    normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import * as Yup from "yup";
import { toast } from "react-toastify";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormFeedback,
    Input,
    Row,
} from "reactstrap";
import CommonTable from "components/Common/TableContainer";
import { submitCallScheduledReportFilterData } from "model/CallScheduledReportModel";
import { useCallScheduledReportContext } from "libs/context/CallScheduledReportContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import Loaders from "components/Common/Loaders";

const callScheduledReportFilterSchema = Yup.object().shape({
    fromDate: Yup.string().trim().required("Start date is required"),
    toDate: Yup.string().trim().required("End date is required"),
});

const FilterFieldFeedback = ({ message = "" }) => (
    <div className="filter-field-feedback-slot" aria-live="polite">
        {message ? (
            <FormFeedback type="invalid" className="d-block filter-field-feedback mb-0">
                {message}
            </FormFeedback>
        ) : null}
    </div>
);

FilterFieldFeedback.propTypes = {
    message: PropTypes.string,
};

const toFilterDate = (value) => normalizeMetadataFilterDateForApi(value);

const normalizeCallScheduledReportFilterPayload = (raw = {}) => {
    const out = { ...raw };
    out.fromDate = toFilterDate(out.fromDate);
    out.toDate = toFilterDate(out.toDate);
    return out;
};

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data && typeof responseData.data === "object"
            ? responseData.data
            : responseData;

    const rows = Array.isArray(nestedData?.rows)
        ? nestedData.rows
        : Array.isArray(responseData?.rows)
            ? responseData.rows
            : Array.isArray(nestedData?.records)
                ? nestedData.records
                : Array.isArray(responseData?.records)
                    ? responseData.records
                    : [];

    const grandTotal =
        nestedData?.grandTotal ??
        responseData?.grandTotal ??
        null;

    return { rows, grandTotal };
};

const mapRowsFromApi = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((item = {}, index) => ({
        ...item,
        id: item?.id ?? item?._id ?? `row-${item?.serialNumber ?? index}`,
        serialNumber: item?.serialNumber ?? index + 1,
        callStage: item?.callStage ?? "",
        totalCallTrigger: item?.totalCallTrigger ?? 0,
    }));

const CallScheduledReport = () => {
    const { fetchCallScheduledReportContextData, callScheduledReportContextData } =
        useCallScheduledReportContext() || {};

    const [reportData, setReportData] = useState([]);
    const [grandTotal, setGrandTotal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tableMetaData, setTableMetadata] = useState();
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
    const [filterForm, setFilterForm] = useState({
        fromDate: "",
        toDate: "",
    });
    const [filterErrors, setFilterErrors] = useState({});

    const filteredData = useMemo(() => {
        if (grandTotal == null) return reportData;
        return [
            ...reportData,
            {
                id: "__grand_total__",
                _isGrandTotalRow: true,
                serialNumber: "",
                callStage: "Grand Total",
                totalCallTrigger: grandTotal,
            },
        ];
    }, [reportData, grandTotal]);

    useEffect(() => {
        const valueReModified = callScheduledReportContextData?.table?.columns?.map((col) => ({
            Header: col?.title,
            accessor: col?.field,
        }));
        setTableMetadata(valueReModified);
    }, [callScheduledReportContextData]);

    const buildPayloadFromUpperForm = (form = {}) =>
        normalizeCallScheduledReportFilterPayload({
            fromDate: form?.fromDate || null,
            toDate: form?.toDate || null,
        });

    const getCallScheduledReport = async (payloadOverrides = {}) => {
        setLoading(true);
        try {
            const payload = normalizeCallScheduledReportFilterPayload(payloadOverrides);
            const response = await submitCallScheduledReportFilterData(payload);
            const { rows, grandTotal: total } = extractFilterResponse(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                setGrandTotal(null);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            setReportData(mapRowsFromApi(rows));
            setGrandTotal(total);
        } catch (error) {
            toast.error(error?.message || "Unable to load call scheduled report");
            setReportData([]);
            setGrandTotal(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCallScheduledReportContextData?.();
    }, []);

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previous) => ({
            ...previous,
            [name]: value,
        }));
        if (filterErrors?.[name]) {
            setFilterErrors((previous) => ({
                ...previous,
                [name]: "",
            }));
        }
    };

    const handleUpperSearch = async () => {
        try {
            await callScheduledReportFilterSchema.validate(filterForm, { abortEarly: false });
            setFilterErrors({});
        } catch (validationError) {
            const nextErrors = {};
            validationError?.inner?.forEach((error) => {
                nextErrors[error.path] = error.message;
            });
            setFilterErrors(nextErrors);
            return;
        }

        await getCallScheduledReport(buildPayloadFromUpperForm(filterForm));
    };

    const handleFilterSubmit = async (data) => {
        await getCallScheduledReport(data);
        setCommonFilterModalOpen(false);
    };

    return (
        <React.Fragment>
            <div className="page-content call-scheduled-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Call Scheduled Report</h5>
                            <hr />

                            <LegacyFilterToolbar className="w-100">
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Start Date</span>
                                        <Input
                                            type="date"
                                            name="fromDate"
                                            className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                filterErrors?.fromDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.fromDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.fromDate} />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">End Date</span>
                                        <Input
                                            type="date"
                                            name="toDate"
                                            className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                filterErrors?.toDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.toDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.toDate} />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span
                                            className="manage-user-filter-label"
                                            aria-hidden="true"
                                        >
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={handleUpperSearch}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                        <FilterFieldFeedback />
                                    </Col>
                                </Row>
                            </LegacyFilterToolbar>

                            {loading ? (
                                <Loaders />
                            ) : tableMetaData?.length ? (
                                <CommonTable
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={false}
                                    isCheckboxLeftSide={false}
                                    setCommonFilterModalOpen={setCommonFilterModalOpen}
                                    pageName="Call Scheduled Report"
                                />
                            ) : (
                                <Loaders />
                            )}
                        </CardBody>
                    </Card>

                    <AllCommonFilterModal
                        setCommonFilterModalOpen={setCommonFilterModalOpen}
                        commonFilterModalOpen={commonFilterModalOpen}
                        modalTitle="Call Scheduled Report"
                        handleSubmit={handleFilterSubmit}
                        filterData={callScheduledReportContextData?.filters}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

CallScheduledReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(CallScheduledReport);
